/**
 * Catalogue des permissions atomiques (`ressource.action`).
 * Référence : document 04 § 7.1 — catalogue socle étendu par les incréments livrés
 * (`opportunity` avec le pipeline commercial, `activity` et `task` avec les activités
 * et tâches CRM — E08 ; `calendar` porte l'agenda, E26 ; `category`, `unit`, `vat_rate`,
 * `payment_method`, `payment_term` et `text_block` portent les référentiels du catalogue — E09/E10 ;
 * `supplier` porte les fournisseurs et leurs conditions d'achat — E09-03 ;
 * `order` porte la chaîne documentaire des commandes, préparée avec le module Devis — E11).
 * La table compte 105 permissions.
 * Cette table est la source unique : les seeders, l'API et le frontend en dérivent.
 */
export const PERMISSION_CATALOG = [
    { code: 'crm.read', module: 'crm', action: 'read', description: 'Consulter les données CRM' },
    { code: 'crm.create', module: 'crm', action: 'create', description: 'Créer des données CRM' },
    { code: 'crm.update', module: 'crm', action: 'update', description: 'Modifier les données CRM' },
    { code: 'crm.delete', module: 'crm', action: 'delete', description: 'Supprimer des données CRM' },
    { code: 'crm.export', module: 'crm', action: 'export', description: 'Exporter les données CRM' },
    { code: 'client.read', module: 'client', action: 'read', description: 'Consulter les clients' },
    { code: 'client.create', module: 'client', action: 'create', description: 'Créer un client' },
    { code: 'client.update', module: 'client', action: 'update', description: 'Modifier un client' },
    {
        code: 'client.delete',
        module: 'client',
        action: 'delete',
        description: 'Supprimer un client',
    },
    {
        code: 'client.export',
        module: 'client',
        action: 'export',
        description: 'Exporter les clients',
    },
    {
        code: 'prospect.read',
        module: 'prospect',
        action: 'read',
        description: 'Consulter les prospects',
    },
    {
        code: 'prospect.create',
        module: 'prospect',
        action: 'create',
        description: 'Créer un prospect',
    },
    {
        code: 'prospect.update',
        module: 'prospect',
        action: 'update',
        description: 'Modifier un prospect',
    },
    {
        code: 'prospect.delete',
        module: 'prospect',
        action: 'delete',
        description: 'Supprimer un prospect',
    },
    {
        code: 'prospect.validate',
        module: 'prospect',
        action: 'validate',
        description: 'Convertir un prospect en client',
    },
    {
        code: 'opportunity.read',
        module: 'opportunity',
        action: 'read',
        description: 'Consulter les opportunités et le pipeline',
    },
    {
        code: 'opportunity.create',
        module: 'opportunity',
        action: 'create',
        description: 'Créer une opportunité',
    },
    {
        code: 'opportunity.update',
        module: 'opportunity',
        action: 'update',
        description: 'Modifier une opportunité et la déplacer sur le pipeline',
    },
    {
        code: 'opportunity.delete',
        module: 'opportunity',
        action: 'delete',
        description: 'Supprimer une opportunité',
    },
    {
        code: 'activity.read',
        module: 'activity',
        action: 'read',
        description: 'Consulter les activités CRM',
    },
    {
        code: 'activity.create',
        module: 'activity',
        action: 'create',
        description: 'Consigner une activité CRM',
    },
    {
        code: 'activity.update',
        module: 'activity',
        action: 'update',
        description: 'Modifier une activité CRM',
    },
    {
        code: 'activity.delete',
        module: 'activity',
        action: 'delete',
        description: 'Supprimer une activité CRM',
    },
    {
        code: 'task.read',
        module: 'task',
        action: 'read',
        description: 'Consulter les tâches et rappels CRM',
    },
    {
        code: 'task.create',
        module: 'task',
        action: 'create',
        description: 'Créer une tâche CRM',
    },
    {
        code: 'task.update',
        module: 'task',
        action: 'update',
        description: 'Modifier une tâche CRM et son statut',
    },
    {
        code: 'task.delete',
        module: 'task',
        action: 'delete',
        description: 'Supprimer une tâche CRM',
    },
    {
        code: 'contact.read',
        module: 'contact',
        action: 'read',
        description: 'Consulter les contacts',
    },
    { code: 'contact.create', module: 'contact', action: 'create', description: 'Créer un contact' },
    {
        code: 'contact.update',
        module: 'contact',
        action: 'update',
        description: 'Modifier un contact',
    },
    {
        code: 'contact.delete',
        module: 'contact',
        action: 'delete',
        description: 'Supprimer un contact',
    },
    {
        code: 'calendar.read',
        module: 'calendar',
        action: 'read',
        description: "Consulter les événements d'agenda",
    },
    {
        code: 'calendar.create',
        module: 'calendar',
        action: 'create',
        description: "Créer un événement d'agenda",
    },
    {
        code: 'calendar.update',
        module: 'calendar',
        action: 'update',
        description: "Modifier un événement d'agenda",
    },
    {
        code: 'calendar.delete',
        module: 'calendar',
        action: 'delete',
        description: "Supprimer un événement d'agenda",
    },
    { code: 'item.read', module: 'item', action: 'read', description: 'Consulter le catalogue' },
    { code: 'item.create', module: 'item', action: 'create', description: 'Créer un article' },
    { code: 'item.update', module: 'item', action: 'update', description: 'Modifier un article' },
    { code: 'item.delete', module: 'item', action: 'delete', description: 'Supprimer un article' },
    {
        code: 'category.manage',
        module: 'category',
        action: 'manage',
        description: 'Gérer les catégories d’articles',
    },
    {
        code: 'unit.manage',
        module: 'unit',
        action: 'manage',
        description: 'Gérer les unités de mesure',
    },
    {
        code: 'vat_rate.manage',
        module: 'vat_rate',
        action: 'manage',
        description: 'Gérer les taux de TVA',
    },
    {
        code: 'payment_method.manage',
        module: 'payment_method',
        action: 'manage',
        description: 'Gérer les modes de règlement',
    },
    {
        code: 'payment_term.manage',
        module: 'payment_term',
        action: 'manage',
        description: 'Gérer les conditions de règlement',
    },
    {
        code: 'text_block.manage',
        module: 'text_block',
        action: 'manage',
        description: 'Gérer les blocs de texte réutilisables',
    },
    {
        code: 'supplier.read',
        module: 'supplier',
        action: 'read',
        description: 'Consulter les fournisseurs',
    },
    {
        code: 'supplier.manage',
        module: 'supplier',
        action: 'manage',
        description: 'Gérer les fournisseurs et leurs conditions d’achat',
    },
    { code: 'quote.read', module: 'quote', action: 'read', description: 'Consulter les devis' },
    { code: 'quote.create', module: 'quote', action: 'create', description: 'Créer un devis' },
    { code: 'quote.update', module: 'quote', action: 'update', description: 'Modifier un devis' },
    { code: 'quote.delete', module: 'quote', action: 'delete', description: 'Supprimer un devis' },
    {
        code: 'quote.validate',
        module: 'quote',
        action: 'validate',
        description: 'Valider / faire accepter un devis',
    },
    { code: 'quote.export', module: 'quote', action: 'export', description: 'Exporter les devis' },
    { code: 'order.read', module: 'order', action: 'read', description: 'Consulter les commandes' },
    { code: 'order.create', module: 'order', action: 'create', description: 'Créer une commande' },
    {
        code: 'order.update',
        module: 'order',
        action: 'update',
        description: 'Modifier une commande',
    },
    {
        code: 'order.delete',
        module: 'order',
        action: 'delete',
        description: 'Supprimer une commande',
    },
    {
        code: 'order.validate',
        module: 'order',
        action: 'validate',
        description: 'Valider une commande',
    },
    {
        code: 'order.export',
        module: 'order',
        action: 'export',
        description: 'Exporter les commandes',
    },
    {
        code: 'invoice.read',
        module: 'invoice',
        action: 'read',
        description: 'Consulter les factures',
    },
    { code: 'invoice.create', module: 'invoice', action: 'create', description: 'Créer une facture' },
    {
        code: 'invoice.update',
        module: 'invoice',
        action: 'update',
        description: 'Modifier une facture',
    },
    {
        code: 'invoice.delete',
        module: 'invoice',
        action: 'delete',
        description: 'Supprimer une facture',
    },
    {
        code: 'invoice.validate',
        module: 'invoice',
        action: 'validate',
        description: 'Valider / émettre une facture',
    },
    {
        code: 'invoice.export',
        module: 'invoice',
        action: 'export',
        description: 'Exporter les factures',
    },
    {
        code: 'payment.read',
        module: 'payment',
        action: 'read',
        description: 'Consulter les paiements',
    },
    {
        code: 'payment.create',
        module: 'payment',
        action: 'create',
        description: 'Saisir un paiement',
    },
    {
        code: 'payment.update',
        module: 'payment',
        action: 'update',
        description: 'Modifier un paiement',
    },
    {
        code: 'payment.delete',
        module: 'payment',
        action: 'delete',
        description: 'Supprimer un paiement',
    },
    {
        code: 'reminder.read',
        module: 'reminder',
        action: 'read',
        description: 'Consulter les relances',
    },
    {
        code: 'reminder.create',
        module: 'reminder',
        action: 'create',
        description: 'Créer une relance',
    },
    {
        code: 'reminder.update',
        module: 'reminder',
        action: 'update',
        description: 'Modifier une relance',
    },
    {
        code: 'reminder.delete',
        module: 'reminder',
        action: 'delete',
        description: 'Supprimer une relance',
    },
    {
        code: 'treasury.read',
        module: 'treasury',
        action: 'read',
        description: 'Consulter la trésorerie',
    },
    {
        code: 'treasury.create',
        module: 'treasury',
        action: 'create',
        description: 'Créer une prévision',
    },
    {
        code: 'treasury.update',
        module: 'treasury',
        action: 'update',
        description: 'Modifier une prévision',
    },
    {
        code: 'treasury.delete',
        module: 'treasury',
        action: 'delete',
        description: 'Supprimer une prévision',
    },
    {
        code: 'treasury.export',
        module: 'treasury',
        action: 'export',
        description: 'Exporter la trésorerie',
    },
    {
        code: 'banking.read',
        module: 'banking',
        action: 'read',
        description: 'Consulter les comptes et transactions',
    },
    {
        code: 'banking.create',
        module: 'banking',
        action: 'create',
        description: 'Connecter une banque',
    },
    {
        code: 'banking.update',
        module: 'banking',
        action: 'update',
        description: 'Rapprocher les transactions',
    },
    {
        code: 'banking.delete',
        module: 'banking',
        action: 'delete',
        description: 'Déconnecter une banque',
    },
    { code: 'ged.read', module: 'ged', action: 'read', description: 'Consulter les documents' },
    { code: 'ged.create', module: 'ged', action: 'create', description: 'Déposer un document' },
    {
        code: 'ged.update',
        module: 'ged',
        action: 'update',
        description: 'Modifier / partager un document',
    },
    { code: 'ged.delete', module: 'ged', action: 'delete', description: 'Supprimer un document' },
    {
        code: 'portal.read',
        module: 'portal',
        action: 'read',
        description: 'Consulter le portail client',
    },
    {
        code: 'portal.create',
        module: 'portal',
        action: 'create',
        description: 'Inviter un accès portail',
    },
    {
        code: 'portal.update',
        module: 'portal',
        action: 'update',
        description: 'Gérer les accès portail',
    },
    {
        code: 'portal.delete',
        module: 'portal',
        action: 'delete',
        description: 'Révoquer un accès portail',
    },
    {
        code: 'signature.read',
        module: 'signature',
        action: 'read',
        description: 'Consulter les demandes de signature',
    },
    {
        code: 'signature.create',
        module: 'signature',
        action: 'create',
        description: 'Envoyer en signature',
    },
    {
        code: 'signature.update',
        module: 'signature',
        action: 'update',
        description: 'Gérer une demande de signature',
    },
    {
        code: 'signature.delete',
        module: 'signature',
        action: 'delete',
        description: 'Annuler une demande de signature',
    },
    {
        code: 'e_invoicing.read',
        module: 'e_invoicing',
        action: 'read',
        description: 'Consulter les dépôts e-invoicing',
    },
    {
        code: 'e_invoicing.create',
        module: 'e_invoicing',
        action: 'create',
        description: 'Transmettre une facture électronique',
    },
    {
        code: 'e_invoicing.update',
        module: 'e_invoicing',
        action: 'update',
        description: 'Relancer un dépôt e-invoicing',
    },
    {
        code: 'settings.read',
        module: 'settings',
        action: 'read',
        description: 'Consulter les paramètres',
    },
    {
        code: 'settings.update',
        module: 'settings',
        action: 'update',
        description: 'Modifier les paramètres',
    },
    {
        code: 'admin.read',
        module: 'admin',
        action: 'read',
        description: "Consulter l'administration",
    },
    {
        code: 'admin.create',
        module: 'admin',
        action: 'create',
        description: 'Créer des comptes et rôles',
    },
    {
        code: 'admin.update',
        module: 'admin',
        action: 'update',
        description: 'Modifier les comptes et rôles',
    },
    {
        code: 'admin.delete',
        module: 'admin',
        action: 'delete',
        description: 'Désactiver des comptes et rôles',
    },
    { code: 'report.read', module: 'report', action: 'read', description: 'Consulter les rapports' },
    {
        code: 'report.export',
        module: 'report',
        action: 'export',
        description: 'Exporter les rapports',
    },
];
/** Tous les codes de permission, dans l'ordre du catalogue. */
export const PERMISSION_CODES = PERMISSION_CATALOG.map((permission) => permission.code);
const PERMISSION_BY_CODE = new Map(PERMISSION_CATALOG.map((permission) => [permission.code, permission]));
export function getPermissionDefinition(code) {
    return PERMISSION_BY_CODE.get(code);
}
export function isPermissionCode(value) {
    return PERMISSION_BY_CODE.has(value);
}
/** Regroupe les permissions par module, dans l'ordre du catalogue. */
export function permissionsByModule() {
    const grouped = {};
    for (const permission of PERMISSION_CATALOG) {
        const bucket = grouped[permission.module] ?? (grouped[permission.module] = []);
        bucket.push(permission.code);
    }
    return grouped;
}
//# sourceMappingURL=permissions.map.js.map